/* eslint-disable max-statements */
import { useSelector } from 'react-redux';
import { Status as StatusEnum } from '@tiendanube/common/src/enums';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { useAppDispatch } from 'App/store';
import { useToast } from 'commons/hooks';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import {
  trackingNuvemEnvioCreditRechargeLabelsModal,
  trackingNuvemEnvioPrintLabels,
} from 'domains/NuvemEnvioCommons/tracking';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  openNuvemEnvioPrintLabelModal as openNuvemEnvioPrintLabelModalAction,
  closeNuvemEnvioPrintLabelModal as closeNuvemEnvioPrintLabelModalAction,
} from '../../ordersSlice';
import { getShowPrintLabelModal } from '../../ordersSlice/ordersSelectors';
import { extractFulfillmentOrdersIds, OrderForPrintLabel } from '../../utils';
import useGoToPrintNuvemEnvioLabelsPage from '../useGoToPrintNuvemEnvioLabels/useGoToPrintNuvemEnvioLabels';
import useSimulateNuvemEnvioLabels from '../useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';

export type { OrderForPrintLabel };

interface UseNuvemEnvioPrintLabelResult {
  printNuvemEnvioLabelsFromOrdersList: (
    orders: OrderForPrintLabel[],
  ) => Promise<void>;
  printNuvemEnvioLabels: (
    source: NE_PRINT_LABEL_SOURCE,
    fulfillmentOrderIds: string[],
    isMassivePrint?: boolean,
    paymentStatus?: StatusEnum,
  ) => Promise<void>;
  showNuvemEnvioPrintLabelModal: boolean;
  closeNuvemEnvioPrintLabelModal: () => void;
}

function useNuvemEnvioPrintLabel(): UseNuvemEnvioPrintLabelResult {
  const { id: storeId } = useStoreInfo();
  const { isPostPaid, shouldShowAsyncProcessing, nuvemEnvioConfigs } =
    useGetNuvemEnvioConfigs();
  const { goToPrintNuvemEnvioLabelsPage } = useGoToPrintNuvemEnvioLabelsPage();
  const { simulateNuvemEnvioLabels } = useSimulateNuvemEnvioLabels();
  const t = useNuvemEnvioTranslations();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const dispatch = useAppDispatch();

  const showNuvemEnvioPrintLabelModal = useSelector(getShowPrintLabelModal);

  const closeNuvemEnvioPrintLabelModal = () => {
    dispatch(closeNuvemEnvioPrintLabelModalAction());
  };

  const printNuvemEnvioLabelsFromOrdersList = async (
    orders: OrderForPrintLabel[],
  ) => {
    const fulfillmentOrderIds = extractFulfillmentOrdersIds(orders);

    if (fulfillmentOrderIds.length === 0) {
      addToast({
        label: t('unpaidOrderSelected.toast'),
        appearance: 'danger',
      });
      return;
    }

    printNuvemEnvioLabels(
      NE_PRINT_LABEL_SOURCE.ORDERS_LIST,
      fulfillmentOrderIds,
      true,
    );
  };

  const printNuvemEnvioLabels = async (
    source: NE_PRINT_LABEL_SOURCE,
    fulfillmentOrderIds: string[],
    isMassivePrint = false,
    paymentStatus?: StatusEnum,
  ) => {
    if (paymentStatus && paymentStatus !== StatusEnum.PAID) {
      addToast({
        label: t('unpaidOrderSelected.toast'),
        appearance: 'danger',
      });
      return;
    }

    if (isPostPaid) {
      if (nuvemEnvioConfigs.displaySwitches?.usePrintLabelDocumentsPage) {
        trackingNuvemEnvioPrintLabels(storeId);
      }
      goToPrintNuvemEnvioLabelsPage({
        source,
        fulfillmentOrderIds,
        asyncProcess: isMassivePrint ? false : shouldShowAsyncProcessing,
      });

      return;
    }

    try {
      addToastProgress({
        label: t('creditRechargeNuvemEnvioLabels.toast.wait'),
      });

      const response = await simulateNuvemEnvioLabels(fulfillmentOrderIds);

      if (response.balanceInfo?.shouldRecharge) {
        trackingNuvemEnvioCreditRechargeLabelsModal(
          storeId,
          fulfillmentOrderIds.length,
          response.balanceInfo?.suggestedRechargeAmount ?? 0,
          response.balanceInfo?.currentBalanceAmount ?? 0,
        );

        dispatch(openNuvemEnvioPrintLabelModalAction());
      } else {
        trackingNuvemEnvioPrintLabels(storeId);
        goToPrintNuvemEnvioLabelsPage({
          source,
          fulfillmentOrderIds,
          asyncProcess: shouldShowAsyncProcessing,
        });
      }
    } catch (error) {
      addToast({
        label: t('creditRechargeNuvemEnvioLabels.error.title'),
        appearance: 'danger',
      });
    } finally {
      closeToastProgress();
    }
  };

  return {
    printNuvemEnvioLabelsFromOrdersList,
    printNuvemEnvioLabels,
    showNuvemEnvioPrintLabelModal,
    closeNuvemEnvioPrintLabelModal,
  };
}

export default useNuvemEnvioPrintLabel;
